<script setup>
import { ref, computed, onMounted, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { isToday, isPast } from 'date-fns';
import { debounce } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { useTasksStore } from 'dashboard/stores/tasks';
import { taskKey, getTaskDueDate } from 'dashboard/helper/taskRecurrence';

import TasksListLayout from 'dashboard/components-next/Tasks/TasksListLayout.vue';
import TaskFormDialog from 'dashboard/components-next/Tasks/TaskFormDialog.vue';
import TaskDetailsDialog from 'dashboard/components-next/Tasks/TaskDetailsDialog.vue';
import TaskEmptyState from 'dashboard/components-next/Tasks/TaskEmptyState.vue';
import TaskListItem from 'dashboard/components-next/Tasks/TaskListItem.vue';
import TaskCalendarView from 'dashboard/components-next/Tasks/TaskCalendarView.vue';
import TaskStatsCards from 'dashboard/components-next/Tasks/TaskStatsCards.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';

const DEBOUNCE_DELAY = 300;

const tasksStore = useTasksStore();
const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const formDialogRef = ref(null);
const detailsDialogRef = ref(null);
const calendarViewRef = ref(null);
const statsCardsRef = ref(null);
const currentUser = useMapGetter('getCurrentUser');
const selectedTask = ref(null);
const visibleRange = ref(null);

const tasks = computed(() => tasksStore.getTasksList);
const currentSelectedTask = computed(
  () =>
    tasks.value.find(
      task =>
        selectedTask.value && taskKey(task) === taskKey(selectedTask.value)
    ) ?? selectedTask.value
);
const isFetchingList = computed(() => tasksStore.uiFlags.fetchingList);
const isCreating = computed(() => tasksStore.uiFlags.creatingItem);
const isUpdating = computed(() => tasksStore.uiFlags.updatingItem);
const isDeleting = computed(() => tasksStore.uiFlags.deletingItem);

const searchValue = ref('');
const viewMode = ref('week');
const initialLoaded = ref(false);

const windowFilters = computed(() => {
  if (viewMode.value === 'list' || !visibleRange.value) return {};
  return {
    start: Math.floor(visibleRange.value.start.getTime() / 1000),
    end: Math.floor(visibleRange.value.end.getTime() / 1000),
  };
});

const refreshTasks = () => tasksStore.get(windowFilters.value);

const refreshStats = () => {
  statsCardsRef.value?.fetchStats();
  store.dispatch('taskStats/get', currentUser.value.id);
};

const handleRangeChange = range => {
  const current = visibleRange.value;
  if (
    current &&
    current.start.getTime() === range.start.getTime() &&
    current.end.getTime() === range.end.getTime()
  ) {
    return;
  }
  visibleRange.value = range;
  refreshTasks();
};

watch(viewMode, mode => {
  if (mode === 'list') refreshTasks();
});

const filteredTasks = computed(() => {
  if (!searchValue.value) return tasks.value;
  const query = searchValue.value.toLowerCase();
  return tasks.value.filter(task => task.title.toLowerCase().includes(query));
});

const STATUS_FILTER_OPTIONS = ['pending', 'completed', 'all'];

const statusFilter = ref('pending');

const statusFilterOptions = computed(() =>
  STATUS_FILTER_OPTIONS.map(value => ({
    value,
    label: t(`TASKS.LIST.STATUS_FILTER.OPTIONS.${value.toUpperCase()}`),
  }))
);

const statusFilterLabel = computed(
  () =>
    statusFilterOptions.value.find(
      option => option.value === statusFilter.value
    )?.label
);

// Filtro de responsavel: "Todos" + um item por usuario (valor em texto, o
// SelectMenu so aceita String).
const ASSIGNEE_ALL = 'all';
const assigneeFilter = ref(ASSIGNEE_ALL);
const selectedAssigneeId = computed(() =>
  assigneeFilter.value === ASSIGNEE_ALL ? null : Number(assigneeFilter.value)
);

const assigneeOptions = computed(() => [
  { value: ASSIGNEE_ALL, label: t('TASKS.LIST.ASSIGNEE_FILTER.ALL') },
  ...(store.getters['agents/getAgents'] || []).map(agent => ({
    value: String(agent.id),
    label: agent.name,
  })),
]);

const assigneeFilterLabel = computed(
  () =>
    assigneeOptions.value.find(option => option.value === assigneeFilter.value)
      ?.label
);

// Filtro por card de resumo (hoje/atrasadas/futuras) - ver TaskStatsCards.
const dueBucketFilter = ref(null);

const classifyDueBucket = task => {
  const dueDate = getTaskDueDate(task);
  if (isPast(dueDate) && !isToday(dueDate)) return 'overdue';
  if (isToday(dueDate)) return 'dueToday';
  return 'upcoming';
};

const toggleDueBucketFilter = bucket => {
  dueBucketFilter.value = dueBucketFilter.value === bucket ? null : bucket;
  // The 3 buckets only make sense for open tasks.
  if (dueBucketFilter.value) {
    statusFilter.value = 'pending';
    // The bucket tasks are listed (and fetched without a date window) only in list view.
    viewMode.value = 'list';
  }
};

const sortedTasks = computed(() => {
  const scopedByStatus =
    statusFilter.value === 'all'
      ? filteredTasks.value
      : filteredTasks.value.filter(task => task.status === statusFilter.value);
  const scopedByAssignee = !selectedAssigneeId.value
    ? scopedByStatus
    : scopedByStatus.filter(
        task => task.assignedToId === selectedAssigneeId.value
      );
  const scoped = !dueBucketFilter.value
    ? scopedByAssignee
    : scopedByAssignee.filter(
        task =>
          task.status === 'pending' &&
          classifyDueBucket(task) === dueBucketFilter.value
      );
  return [...scoped].sort((a, b) => a.dueDate - b.dueDate);
});

const showEmptyStateLayout = computed(
  () => !isFetchingList.value && tasks.value.length === 0
);

const searchResultsOutsideRange = computed(() => {
  if (viewMode.value === 'list') return [];
  if (!searchValue.value || !visibleRange.value) return [];
  const { start, end } = visibleRange.value;
  return sortedTasks.value.filter(task => {
    const dueDate = getTaskDueDate(task);
    return dueDate < start || dueDate >= end;
  });
});

const onSearch = debounce(query => {
  searchValue.value = query;
}, DEBOUNCE_DELAY);

const openCreateDialog = () => {
  formDialogRef.value?.openDialog();
};

const openCreateDialogOnDate = (date, endTime) => {
  formDialogRef.value?.openDialog(date, endTime);
};

const handleCreate = async taskData => {
  try {
    await tasksStore.create(taskData);
    refreshTasks();
    refreshStats();
    formDialogRef.value?.onSuccess();
    useAlert(t('TASKS.CREATE.MESSAGES.SUCCESS'));
  } catch {
    useAlert(t('TASKS.CREATE.MESSAGES.ERROR'));
  }
};

const expandedTaskId = ref(null);

const toggleExpanded = id => {
  expandedTaskId.value = expandedTaskId.value === id ? null : id;
};

const handleUpdateTask = () => {
  useAlert(t('TASKS.LIST.UPDATE.SUCCESS_MESSAGE'));
  refreshTasks();
  refreshStats();
};

const toggleTaskStatus = async task => {
  const newStatus = task.status === 'completed' ? 'pending' : 'completed';
  const seriesScope = task.recurrenceRule
    ? { scope: 'this', occurrenceDate: task.occurrenceDate }
    : {};
  try {
    await tasksStore.update({ id: task.id, status: newStatus, ...seriesScope });
    useAlert(t('TASKS.LIST.UPDATE.SUCCESS_MESSAGE'));
    if (task.recurrenceRule) refreshTasks();
    refreshStats();
  } catch (error) {
    useAlert(error.message ?? t('TASKS.LIST.UPDATE.ERROR_MESSAGE'));
  }
};

const handleDeleteTask = async (id, options) => {
  try {
    await tasksStore.delete(id, options);
    if (options?.scope) refreshTasks();
    refreshStats();
    if (expandedTaskId.value === id) expandedTaskId.value = null;
    if (selectedTask.value?.id === id) detailsDialogRef.value?.close();
    useAlert(t('TASKS.LIST.DELETE.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error.message ?? t('TASKS.LIST.DELETE.ERROR_MESSAGE'));
  }
};

const onSelectTaskFromCalendar = task => {
  selectedTask.value = task;
  detailsDialogRef.value?.open();
};

const jumpToSearchResult = task => {
  calendarViewRef.value?.gotoDate(getTaskDueDate(task));
  onSelectTaskFromCalendar(task);
};

const toggleSelectedTaskStatus = () => {
  if (selectedTask.value) toggleTaskStatus(selectedTask.value);
};

const handleUpdateSelectedTask = updated => {
  selectedTask.value = updated;
  handleUpdateTask();
  if (updated.recurrenceRule || updated.recurringTaskId) {
    detailsDialogRef.value?.close();
  }
};

const openTaskFromQuery = async () => {
  const taskId = Number(route.query.taskId);
  if (!taskId) return;
  await nextTick();
  const task = tasks.value.find(item => item.id === taskId);
  if (task) jumpToSearchResult(task);
  router.replace({ query: { ...route.query, taskId: undefined } });
};

onMounted(async () => {
  store.dispatch('agents/get');
  await tasksStore.get();
  initialLoaded.value = true;
  openTaskFromQuery();
});
</script>

<template>
  <TasksListLayout
    :search-value="searchValue"
    :header-title="t('TASKS.ALL_TASKS.TITLE')"
    :view-mode="viewMode"
    :search-results="searchResultsOutsideRange"
    @search="onSearch"
    @create="openCreateDialog"
    @update:view-mode="viewMode = $event"
    @select-search-result="jumpToSearchResult"
  >
    <template #stats>
      <TaskStatsCards
        ref="statsCardsRef"
        :assigned-to-id="selectedAssigneeId"
        :active-bucket="dueBucketFilter"
        @select="toggleDueBucketFilter"
      />
    </template>

    <template v-if="!showEmptyStateLayout && !isFetchingList" #filters>
      <div class="flex items-center gap-2">
        <SelectMenu
          v-model="statusFilter"
          :options="statusFilterOptions"
          :label="statusFilterLabel"
          sub-menu-position="bottom"
        />
        <SelectMenu
          v-model="assigneeFilter"
          :options="assigneeOptions"
          :label="assigneeFilterLabel"
          sub-menu-position="bottom"
        />
      </div>
    </template>

    <TaskEmptyState
      v-if="showEmptyStateLayout"
      :title="t('TASKS.EMPTY_STATE.TITLE')"
      :subtitle="t('TASKS.EMPTY_STATE.SUBTITLE')"
      :button-label="t('TASKS.EMPTY_STATE.BUTTON_LABEL')"
      @create="openCreateDialog"
    />
    <div
      v-else-if="isFetchingList"
      class="flex items-center justify-center p-8"
    >
      <span class="text-n-slate-11 text-base">{{ t('TASKS.LOADING') }}</span>
    </div>
    <div v-else class="flex flex-col gap-4">
      <div
        class="flex items-center gap-4 px-6 text-xs font-medium uppercase tracking-wide text-n-slate-10"
      >
        <span class="flex-1 min-w-0">{{ t('TASKS.LIST.COLUMNS.TITLE') }}</span>
        <span class="flex-1 min-w-0">{{
          t('TASKS.LIST.COLUMNS.LINKED_TO')
        }}</span>
        <span class="w-40 shrink-0">{{
          t('TASKS.LIST.COLUMNS.DUE_DATE')
        }}</span>
        <span class="w-32 shrink-0">{{
          t('TASKS.LIST.COLUMNS.RESPONSIBLE')
        }}</span>
        <span class="w-9 shrink-0" />
      </div>
      <TaskListItem
        v-for="task in sortedTasks"
        :key="task.id"
        :task="task"
        :is-expanded="expandedTaskId === task.id"
        :is-updating="isUpdating"
        :is-deleting="isDeleting"
        @toggle="toggleExpanded(task.id)"
        @toggle-status="toggleTaskStatus(task)"
        @update-task="handleUpdateTask"
        @delete-task="handleDeleteTask"
      />
      <p
        v-if="!sortedTasks.length"
        class="px-6 py-8 text-sm text-center text-n-slate-11"
      >
        {{ t('TASKS.LIST.STATUS_FILTER.EMPTY_MESSAGE') }}
      </p>
    </div>

    <template #calendar>
      <TaskCalendarView
        v-if="initialLoaded"
        ref="calendarViewRef"
        :tasks="sortedTasks"
        :view-mode="viewMode"
        @select-task="onSelectTaskFromCalendar"
        @create-on-date="openCreateDialogOnDate"
        @range-change="handleRangeChange"
      />
    </template>

    <template #dialogs>
      <TaskFormDialog
        ref="formDialogRef"
        :is-loading="isCreating"
        @create="handleCreate"
        @close="calendarViewRef?.clearDraft()"
      />

      <TaskDetailsDialog
        ref="detailsDialogRef"
        :task="currentSelectedTask"
        :is-updating="isUpdating"
        :is-deleting="isDeleting"
        @toggle-status="toggleSelectedTaskStatus"
        @update-task="handleUpdateSelectedTask"
        @delete-task="handleDeleteTask"
      />
    </template>
  </TasksListLayout>
</template>
